import { Link } from 'react-router'
import { RecostLogo } from '@/components/RecostLogo'
import { MailingListModal } from '@/components/landing/mailing-list-modal'
import { colors } from '@/lib/tokens'

type FooterLink =
  | { label: string; href: string; external?: boolean }
  | { label: string; modal: 'mailing-list' }

const LINKS: Record<string, FooterLink[]> = {
  Product: [
    { label: 'Overview', href: '/overview' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Docs', href: '/docs' },
    { label: 'Changelog', href: '/changelog' },
  ],
  Developers: [
    { label: 'GitHub', href: 'https://github.com/recost-dev/', external: true },
    { label: 'Get Updates', modal: 'mailing-list' },
    { label: 'Feedback', href: '/feedback' },
  ],
  Company: [
    { label: 'Privacy', href: '/privacy' },
    { label: 'Terms', href: '/terms' },
  ],
  Connect: [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/recostdev', external: true },
    { label: 'Email', href: 'mailto:hello@recost.dev', external: true },
    { label: 'X', href: 'https://x.com/recost_dev', external: true },
  ],
}

export function Footer() {
  return (
    <footer
      style={{
        backgroundColor: colors.bgPage,
        borderTop: `1px solid ${colors.borderDefault}`,
        padding: '48px 20px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="glow-orb" style={{ width: 400, height: 300, top: -60, right: '15%' }} />
      <div className="glow-orb-teal" style={{ width: 300, height: 300, bottom: -40, left: '10%' }} />
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Top row: brand + links */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '210px minmax(0, 1fr)',
            gap: 44,
            marginBottom: 32,
          }}
          className="footer-grid"
        >
          {/* Brand */}
          <div>
            <Link to="/" style={{ textDecoration: 'none', display: 'inline-block', marginBottom: 12 }}>
              <RecostLogo size={24} />
            </Link>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: colors.textMuted, margin: 0, maxWidth: 170 }}>
              AI cost intelligence for developers.
            </p>
          </div>

          {/* Link columns */}
          <div
            className="footer-links-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
              gap: 28,
            }}
          >
            {Object.entries(LINKS).map(([group, items]) => (
              <div key={group}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: colors.textPrimary,
                    margin: '0 0 14px',
                  }}
                >
                  {group}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {items.map((item) =>
                    'modal' in item ? (
                      <MailingListModal key={item.label}>
                        <button
                          type="button"
                          style={{
                            fontFamily: 'inherit',
                            fontSize: 13,
                            fontWeight: 400,
                            lineHeight: 'inherit',
                            letterSpacing: 'inherit',
                            color: colors.textMuted,
                            textDecoration: 'none',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                          onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                        >
                          {item.label}
                        </button>
                      </MailingListModal>
                    ) : 'external' in item && item.external ? (
                      <a
                        key={item.label}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 13,
                          color: colors.textMuted,
                          textDecoration: 'none',
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                      >
                        {item.label}
                      </a>
                    ) : item.href.startsWith('#') ? (
                      <a
                        key={item.label}
                        href={item.href}
                        style={{
                          fontSize: 13,
                          color: colors.textMuted,
                          textDecoration: 'none',
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        key={item.label}
                        to={item.href}
                        style={{
                          fontSize: 13,
                          color: colors.textMuted,
                          textDecoration: 'none',
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                      >
                        {item.label}
                      </Link>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            borderTop: `1px solid ${colors.borderDefault}`,
            paddingTop: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>
            &copy; {new Date().getFullYear()} Recost. All rights reserved.
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .footer-grid {
            grid-template-columns: 1fr !important;
          }
          .footer-links-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 24px !important;
          }
        }
      `}</style>
    </footer>
  )
}

